import { useQuery } from "@tanstack/react-query";
import { dashboardApi, useAdminRealtime } from "@/lib/admin-api";
import type { DashboardData, ChartData } from "@/lib/admin-api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DateRangeParams {
  readonly from: Date;
  readonly to: Date;
}

// ---------------------------------------------------------------------------
// Query keys — include ISO strings so react-query caches per-range
// ---------------------------------------------------------------------------

const dashboardKey = ({ from, to }: DateRangeParams) =>
  ["admin", "dashboard", from.toISOString(), to.toISOString()] as const;

const chartsKey = ({ from, to }: DateRangeParams) =>
  ["admin", "dashboard", "charts", from.toISOString(), to.toISOString()] as const;

// ---------------------------------------------------------------------------
// KPIs hook
// ---------------------------------------------------------------------------

export function useDashboardKPIs(range: DateRangeParams) {
  useAdminRealtime("orders", ["admin", "dashboard"]);

  return useQuery<DashboardData>({
    queryKey: [...dashboardKey(range)],
    queryFn: () => dashboardApi.get(range),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

// ---------------------------------------------------------------------------
// Charts hook
// ---------------------------------------------------------------------------

export function useDashboardCharts(range: DateRangeParams) {
  return useQuery<ChartData>({
    queryKey: [...chartsKey(range)],
    queryFn: () => dashboardApi.getCharts(range),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}
